import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import { Col, Row } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import savePassword, { ChangePasswordData } from 'core/application/account/profile/changePassword'

import PasswordInput from 'common/components/dataEntry/formik/FormikDefaultPasswordField'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'
import { generalPasswordRegx } from 'common/components/utils/Regx'

import useCommandRequest from '../../common/hooks/useCommandRequest'

import './styles.less'

const formValidator = (translate: any) =>
  Yup.object({
    currentPassword: Yup.string().required(translate('general.required')),
    newPassword: Yup.string()
      .required(translate('general.required'))
      .min(8, translate('user-header.change-password-form.errors.password-regex'))
      .matches(generalPasswordRegx, translate('user-header.change-password-form.errors.password-regex')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], translate('user-header.change-password-form.errors.confirm-password'))
      .required(translate('general.required')),
  })

interface ChangePasswordForm {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const defaultValues: ChangePasswordForm = {
  confirmPassword: '',
  currentPassword: '',
  newPassword: '',
}

const ChangePassword: React.FC = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()
  const [sendCommand, isCommandLoading] = useCommandRequest()

  const submitData = async (values: ChangePasswordForm) => {
    const dataForSubmit: ChangePasswordData = {
      currentPassword: values.currentPassword,
      newPassword: values.confirmPassword,
    }

    const result = await sendCommand(() => savePassword(dataForSubmit))
    if (result.completed) {
      notification.open({
        message: 'Success',
        description: 'Password changed successfully',
        type: 'success',
      })
      history.goBack()
    }
  }

  return (
    <div className="change-password-wrapper">
      <Row className="inner-padding">
        <Col span={4} className="text-semi-bold">
          {translate('user-header.change-password-form.title')}
        </Col>
      </Row>
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        initialValues={defaultValues}
        onSubmit={(values: any) => submitData(values)}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Row className="inner-padding">
              <Col span={10}>
                <Row>
                  <Col span={24}>
                    <PasswordInput
                      label={translate('user-header.change-password-form.labels.password')}
                      name="currentPassword"
                      setPassword={() => {}}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <PasswordInput
                      label={translate('user-header.change-password-form.labels.new-password')}
                      name="newPassword"
                      popover={true}
                      setPassword={() => {}}
                    />
                  </Col>
                </Row>
                <Row className="pb-8">
                  <Col span={24}>
                    <PasswordInput
                      label={translate('user-header.change-password-form.labels.confirm-password')}
                      name="confirmPassword"
                      setPassword={() => {}}
                    />
                  </Col>
                  <Col>
                    <Button
                      htmlType="submit"
                      type="primary"
                      className="width-100-perc"
                      loading={isCommandLoading}
                      disabled={isCommandLoading}
                    >
                      {translate('user-header.change-password-form.button')}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </form>
        )}
      </Formik>
    </div>
  )
}

export default ChangePassword
